class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    const GiftCard = document.querySelector('gift-package');

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);

      GiftCard?.checkGiftCardPackageRemove(JSON.parse(this.dataset.collections));
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // Initialize background check state
    this.isUpdatingFreeGifts = false;
    this.backgroundCheckTimeout = null;
    this.backgroundCheckAttempts = 0;

    // Start background check for free gifts when cart is loaded
    this.startFreeGiftBackgroundCheck();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
    
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  renderContents(parsedState) {
    const cartMain = document.getElementById('main-cart-items');
    if (cartMain) cartMain.classList.remove('is-empty');

    this.getSectionsToRender().forEach((section) => {
      const elementToReplace =
        document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

      elementToReplace.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.section],
        section.selector
      );
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: 'main-cart-items',
        selector: '.main-cart-inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      }
    ];
  }

  async updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);
  
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } });

      const state = await response.text();
      const parsedState = JSON.parse(state);

      const quantityElement = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');

      if (parsedState.errors) {
        quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, parsedState.errors);
        return;
      }

      // Chain cart updates sequentially to ensure each method receives the most recent state
      let currentState = parsedState;

      // Step 1: Update free gift items
      currentState = await this.updateFreeGiftItems(currentState);

      // Step 2: Update gift wrapping (receives updated state from step 1)
      currentState = await this.checkAndRemoveGiftWrapping(currentState);

      // Step 3: Update monogram fees (receives updated state from step 2)
      currentState = await this.updateMonogramFeeItems(currentState);

      const shippingProtection = document.querySelector('cart-shipping-protection');
      shippingProtection?.updateCartTotal(currentState);

      const shippingProtectionData = await shippingProtection?.updateShippingProtection();

      const newParsedState = shippingProtectionData || currentState;
      const parseStateSections = newParsedState?.sections;

      const cartItemCount = shippingProtectionData?.item_count ?? currentState.item_count;

      const cartDrawerWrapper = document.querySelector('cart-drawer');
      const cartMain = document.getElementById('main-cart-items');

      if (cartMain) cartMain.classList.toggle('is-empty', cartItemCount === 0);
      if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', cartItemCount === 0);

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);

        elementToReplace.innerHTML = this.getSectionInnerHTML(parseStateSections[section.section], section.selector);
      });

      const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
      let message = '';
      if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
        if (typeof updatedValue === 'undefined') {
          message = window.cartStrings.error;
        } else {
          message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
        }
      }
      this.updateLiveRegions(line, message);

      const lineItem = document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
        cartDrawerWrapper
          ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
          : lineItem.querySelector(`[name="${name}"]`).focus();
      } else if (cartItemCount === 0 && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
      } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
      }

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
    } catch (error) {
      this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
      const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      errors.textContent = window.cartStrings.error;
    } finally {
      this.disableLoading(line);
    }
  }

  async updateFreeGiftItems(parsedState) {
    // Set flag to prevent background check from running
    this.isUpdatingFreeGifts = true;
    
    // Clear any pending background check
    if (this.backgroundCheckTimeout) {
      clearTimeout(this.backgroundCheckTimeout);
      this.backgroundCheckTimeout = null;
    }

    const giftItemsData = JSON.parse(document.getElementById('CartFreeGiftItems')?.textContent || '[]');
    if (!giftItemsData.length || !parsedState.items) {
      this.isUpdatingFreeGifts = false;
      this.restartBackgroundCheck();
      return parsedState;
    }

    const cartTotal = parsedState.total_price;
    const itemsToUpdate = {};

    // Check each free gift item to see if it should be removed or quantity adjusted
    parsedState.items.forEach((item) => {
      const giftItem = giftItemsData.find(gift => gift.product_id === item.product_id);
      
      if (giftItem) {
        // If cart total is below threshold, remove the item
        if (cartTotal < giftItem.threshold) {
          itemsToUpdate[item.variant_id] = 0;
        }
        // If quantity is greater than 1, set it to 1
        else if (item.quantity > 1) {
          itemsToUpdate[item.variant_id] = 1;
        }
      }
    });

    // If there are items to update, update the cart
    if (Object.keys(itemsToUpdate).length > 0) {
      try {
        const body = JSON.stringify({
          updates: itemsToUpdate,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        
        if (response.ok) {
          const updatedState = await response.json();
          this.isUpdatingFreeGifts = false;
          this.restartBackgroundCheck();
          return updatedState;
        } else {
          console.error('Failed to update free gift items');
        }
      } catch (error) {
        console.error('Error updating free gift items:', error);
      }
    }

    this.isUpdatingFreeGifts = false;
    this.restartBackgroundCheck();
    return parsedState;
  }

  startFreeGiftBackgroundCheck() {
    this.backgroundCheckAttempts = 0;
    const maxAttempts = 3;
    const checkInterval = 2000; // 2 seconds

    const checkFreeGifts = async () => {
      // Don't run if updateFreeGiftItems is currently executing
      if (this.isUpdatingFreeGifts) {
        this.backgroundCheckTimeout = setTimeout(checkFreeGifts, checkInterval);
        return;
      }

      if (this.backgroundCheckAttempts >= maxAttempts) {
        return;
      }

      this.backgroundCheckAttempts++;

      try {
        // Get current cart state
        const response = await fetch(`${routes.cart_url}.js`);
        if (!response.ok) {
          if (this.backgroundCheckAttempts < maxAttempts) {
            this.backgroundCheckTimeout = setTimeout(checkFreeGifts, checkInterval);
          }
          return;
        }

        const cartData = await response.json();
        const giftItemsData = JSON.parse(document.getElementById('CartFreeGiftItems')?.textContent || '[]');

        if (!giftItemsData.length || !cartData.items) {
          return;
        }

        const cartTotal = cartData.total_price;
        const itemsToUpdate = {};

        // Check each free gift item to see if it should be removed
        cartData.items.forEach((item) => {
          const giftItem = giftItemsData.find(gift => gift.product_id === item.product_id);
          
          if (giftItem && cartTotal < giftItem.threshold) {
            itemsToUpdate[item.variant_id] = 0;
          }
        });

        // If there are items to remove, update the cart
        if (Object.keys(itemsToUpdate).length > 0) {
          const updateBody = JSON.stringify({
            updates: itemsToUpdate,
            sections: this.getSectionsToRender().map((section) => section.section),
            sections_url: window.location.pathname
          });

          const updateResponse = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body: updateBody } });
          
          if (updateResponse.ok) {
            const updatedState = await updateResponse.json();
            this.renderContents(updatedState);
            return; // Stop checking if we successfully removed items
          }
        }

        // If no items were removed and we haven't reached max attempts, schedule next check
        if (this.backgroundCheckAttempts < maxAttempts) {
          this.backgroundCheckTimeout = setTimeout(checkFreeGifts, checkInterval);
        }

      } catch (error) {
        console.error('Error in background free gift check:', error);
        if (this.backgroundCheckAttempts < maxAttempts) {
          this.backgroundCheckTimeout = setTimeout(checkFreeGifts, checkInterval);
        }
      }
    };

    // Start the first check after a short delay
    this.backgroundCheckTimeout = setTimeout(checkFreeGifts, 1000);
  }

  restartBackgroundCheck() {
    // Clear any existing timeout
    if (this.backgroundCheckTimeout) {
      clearTimeout(this.backgroundCheckTimeout);
      this.backgroundCheckTimeout = null;
    }
    
    // Reset attempts counter and restart
    this.backgroundCheckAttempts = 0;
    this.startFreeGiftBackgroundCheck();
  }

  async checkAndRemoveGiftWrapping(parsedState) {
    const productIdScripts = Array.from(document.querySelectorAll('script[data-gift-product-ids]'));
    const legacyProductIdScript = document.getElementById('CartGiftWrappingProducts');

    if (legacyProductIdScript && !legacyProductIdScript.hasAttribute('data-gift-product-ids')) {
      productIdScripts.push(legacyProductIdScript);
    }

    const collectedProductIds = new Set();
    const addProductId = (value) => {
      if (typeof value === 'number' && Number.isFinite(value)) {
        collectedProductIds.add(value);
        return;
      }

      if (typeof value === 'string' && value.trim() !== '') {
        const numericValue = Number(value);

        if (!Number.isNaN(numericValue)) {
          collectedProductIds.add(numericValue);
        }
      }
    };

    productIdScripts.forEach((script) => {
      try {
        const parsedIds = JSON.parse(script.textContent || '[]');

        if (Array.isArray(parsedIds)) {
          parsedIds.forEach(addProductId);
        }
      } catch (error) {
        /* no-op */
      }
    });

    if (typeof window !== 'undefined') {
      try {
        if ('localStorage' in window) {
          const storedValue = window.localStorage.getItem('giftCardProductIds');

          if (storedValue) {
            const storedIds = JSON.parse(storedValue);

            if (Array.isArray(storedIds)) {
              storedIds.forEach(addProductId);
            }
          }
        }
      } catch (error) {
        /* no-op */
      }
    }

    const giftWrappingProductIds = Array.from(collectedProductIds);

    // If no gift wrapping products are configured, skip
    if (!giftWrappingProductIds.length || !parsedState.items || !parsedState.items.length) {
      return parsedState;
    }

    const itemsToUpdate = {};
    const parentVariantPropertyKeys = [
      '__gift_parent_variant_id',
      '_gift_parent_variant_id',
      'gift_parent_variant_id',
      'Gift Parent Variant ID',
      'Parent Variant ID',
    ];

    parsedState.items.forEach((item) => {
      if (!giftWrappingProductIds.includes(item.product_id)) {
        return;
      }

      const itemProperties = item.properties || {};
      let parentVariantIdValue;

      for (const key of parentVariantPropertyKeys) {
        if (itemProperties[key]) {
          parentVariantIdValue = itemProperties[key];
          break;
        }
      }

      if (!parentVariantIdValue) {
        return;
      }

      const parentVariantId = String(parentVariantIdValue).trim();
      if (!parentVariantId) {
        return;
      }

      const parentStillExists = parsedState.items.some((cartItem) => {
        if (cartItem.key === item.key) {
          return false;
        }

        return String(cartItem.variant_id) === parentVariantId;
      });

      if (!parentStillExists) {
        itemsToUpdate[item.variant_id] = 0;
      }
    });

    // Check if all cart items are gift wrapping products
    const allItemsAreGifts = parsedState.items.every((item) =>
      giftWrappingProductIds.includes(item.product_id)
    );

    if (allItemsAreGifts && parsedState.items.length > 0) {
      parsedState.items.forEach((item) => {
        if (giftWrappingProductIds.includes(item.product_id)) {
          itemsToUpdate[item.variant_id] = 0;
        }
      });
    }

    if (Object.keys(itemsToUpdate).length > 0) {
      try {
        const body = JSON.stringify({
          updates: itemsToUpdate,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        
        if (response.ok) {
          const updatedState = await response.json();
          return updatedState;
        } else {
          console.error('Failed to remove gift wrapping items');
        }
      } catch (error) {
        console.error('Error removing gift wrapping items:', error);
      }
    }

    return parsedState;
  }

  async updateMonogramFeeItems(parsedState) {
    const MONOGRAM_FEE_PRODUCT_ID = parseInt(window.MONOGRAM_FEE_PRODUCT_ID);
    
    if (!MONOGRAM_FEE_PRODUCT_ID || isNaN(MONOGRAM_FEE_PRODUCT_ID)) {
      console.error('Monogram fee product ID not configured or invalid');
      return parsedState;
    }
    
    if (!parsedState.items) {
      return parsedState;
    }

    // Count monogram products (items with "Preview" property)
    let monogramProductCount = 0;
    parsedState.items.forEach((item) => {
      if (item.properties && item.properties.Preview) {
        monogramProductCount += item.quantity;
      }
    });

    // Find existing monogram fee items
    let currentMonogramFeeQuantity = 0;
    let monogramFeeVariantId = null;
    parsedState.items.forEach((item) => {
      if (item.product_id === MONOGRAM_FEE_PRODUCT_ID) {
        currentMonogramFeeQuantity += item.quantity;
        monogramFeeVariantId = item.variant_id;
      }
    });

    // If no monogram products, remove all monogram fee items
    if (monogramProductCount === 0 && currentMonogramFeeQuantity > 0) {
      try {
        const body = JSON.stringify({
          updates: { [monogramFeeVariantId]: 0 },
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        
        if (response.ok) {
          return await response.json();
        }
      } catch (error) {
        console.error('Error removing monogram fee items:', error);
      }
    }

    // If monogram products exist but quantities don't match
    if (monogramProductCount > 0 && monogramProductCount !== currentMonogramFeeQuantity) {
      try {
        // If no monogram fee item exists, we need to add it
        if (!monogramFeeVariantId) {
          // Get monogram fee product data to find the variant ID
          const productResponse = await fetch(`/products/${window.MONOGRAM_FEE_PRODUCT_HANDLE}.js`);
          if (productResponse.ok) {
            const productData = await productResponse.json();
            monogramFeeVariantId = productData.variants[0].id;
          } else {
            console.error('Could not fetch monogram fee product data');
            return parsedState;
          }
        }

        const body = JSON.stringify({
          updates: { [monogramFeeVariantId]: monogramProductCount },
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        });

        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
        
        if (response.ok) {
          return await response.json();
        }
      } catch (error) {
        console.error('Error updating monogram fee items:', error);
      }
    }

    return parsedState;
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  async clearCart() {
    try {
      const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
      mainCartItems.classList.add('cart__items--disabled');

      const body = JSON.stringify({
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_clear_url}`, { 
        ...fetchConfig(), 
        ...{ body } 
      });

      if (!response.ok) {
        throw new Error('Failed to clear cart');
      }

      const parsedState = await response.json();
      this.renderContents(parsedState);

    } catch (error) {
      window.location = window.routes.cart_clear_url;
      console.error('Error clearing cart:', error);     
    } finally {
      const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
      mainCartItems.classList.remove('cart__items--disabled');
    }
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-clear-button')) {
  customElements.define(
    'cart-clear-button',
    class CartClearButton extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.addEventListener('click', (event) => {
          event.preventDefault();
          this.clearCart();
        });
      }

      async clearCart() {
        const cartItems = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
        if (cartItems && typeof cartItems.clearCart === 'function') {
          // Disable button during operation
          this.disabled = true;
          const originalText = this.textContent;
          this.textContent = 'Removing...';

          try {
            await cartItems.clearCart();
          } catch (error) {
            console.error('Error clearing cart:', error);
          } finally {
            // Re-enable button
            this.disabled = false;
            this.textContent = originalText;
          }
        } else {
          console.error('Cart items element not found or clearCart method not available');
        }
      }
    }
  );
}

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            this.updateNote(event.target.value);
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );

        this.note = this.querySelector('[name="note"]');
        this.count = this.querySelector('.cart__note-count');
        
        this.note?.addEventListener('input', this.updateNoteCount.bind(this));
      }

      connectedCallback() {
        if (this.note.value.length > 0 && this.dataset.removeNote === 'true') {
          this.note.value = '';
          this.updateNoteCount();

          if (this.dataset.updateNote === 'true') this.updateNote('');
        }
      }

      updateNoteCount() {
        const remainingChars = 100 - this.note.value.length;
        if (this.count) this.count.textContent = `You have ${remainingChars} characters remaining`;
      }

      updateNote(note) {
        const body = JSON.stringify({ note: note });
        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      }
    }
  );
}

if (!customElements.get('cart-shipping-protection')) {
  customElements.define(
    'cart-shipping-protection',
    class CartShippingProtection extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.checkbox = this.querySelector('.cart-shipping-protection__checkbox input')
        this.checkbox?.addEventListener('change', async () => {
          this.renderShippingProtection();
        });

        const modalToggle = this.querySelector('.shipping-protection__modal-toggle');
        modalToggle?.addEventListener('click', (event) => {
          event.preventDefault();
          document.body.classList.toggle('ship-protec-modal-opened');
        });

        this.updatePrice();
        this.renderShippingProtection();
      }

      async renderShippingProtection() {
        const response = await this.updateShippingProtection();
        if (response) this.updateCart(response);
      }

      async updateShippingProtection() {
        try {
          const product = this.getProductData();
          const variant = this.getCurrentVariant();

          const cartItems = this.getCartItems();
          const cartShippingItems = cartItems.filter(item => item.product_id === product.id);
          const cartHasShippingItem = cartShippingItems.length ? true : false;
          const cartHasCurrentVariant = cartItems.some(item => item.id === variant?.id);

          if (!this.checkbox.checked && !cartHasShippingItem) return;
          this.checkbox.disabled = true;

          let responseData;
      
          if ((!this.checkbox.checked && cartHasShippingItem) || (cartHasShippingItem && !cartHasCurrentVariant) || (cartItems.length == 1 && cartHasShippingItem)) {
            responseData = await this.removeShippingProtection(cartShippingItems);
          }

          if (variant && this.checkbox.checked && !cartHasCurrentVariant) {
            responseData = await this.addShippingProtection(variant);
          }

          return responseData;
        } catch (error) {
          console.error('Error updating shipping protection:', error);
        } finally {
          this.checkbox.disabled = false;
        }
      }
      
      async removeShippingProtection(items) {
        let itemsData = items.reduce((acc, item) => {
          acc[item.id] = 0;
          return acc;
        }, {});

        const body = JSON.stringify({
          updates: itemsData,
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname
        });
  
        const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
  
        if (!response.ok) {
          window.location.reload();
          return;
        }

        return await response.json();
      }

      async addShippingProtection(variant) {
        if (!variant) return;

        const body = JSON.stringify({
          id: variant.id,
          quantity: 1,
          sections: this.getSectionsToRender(),
          sections_url: window.location.pathname,
        });
      
        try {
          const response = await fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } });
      
          if (!response.ok) {
            window.location.reload();
          }
      
          return await response.json();
        } catch (error) {
          window.location.reload();
        }
      }

      getSectionsToRender() {
        const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
        if (cartItems.tagName == 'CART-DRAWER-ITEMS' && typeof cartItems.getSectionsToRender !== 'function') {
          return ['cart-drawer', 'cart-icon-bubble'];
        }

        return cartItems.getSectionsToRender().map((section) => section.section);
      }

      updatePrice() {
        const variant = this.getCurrentVariant();
        if (!variant) return;

        const priceEl = this.querySelector('.cart-shipping-protection__price');
        if (priceEl) priceEl.textContent = `: ${formatMoney(variant.price, window.moneyFormat).replace(/\.00$/, '')}`;
      }

      updateCart(parseState) {
        const cartItems = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
        if (!cartItems) {
          window.location.reload();
          return;
        }

        cartItems.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = cartItems.getSectionInnerHTML(
            parseState.sections[section.section],
            section.selector
          );
        });
      }

      updateCartTotal(parsedState) {
        const cartHTML = parsedState.sections['cart-drawer'] || parsedState.sections['main-cart-items'];
        const parsedDoc = new DOMParser().parseFromString(cartHTML, 'text/html').querySelector('cart-shipping-protection');
        this.dataset.cartTotal = parsedDoc?.dataset.cartTotal;
      }
      
      getCartItems() {
        this.cartItemsData = JSON.parse(this.querySelector('[data-cart-items]')?.textContent || '[]');
        return this.cartItemsData;
      }

      getProductData() {
        this.productData = JSON.parse(this.querySelector('[data-shipping-protection-product]')?.textContent || '{}');
        return this.productData;
      }

      getCurrentVariant() {
        const product = this.getProductData();
        if (!product) return;

        const maxThreshold = parseFloat(this.dataset.maxThreshold);
        const roundingType = this.dataset.roundingType;
        const protectionType = this.dataset.protectionType;
        const cartTotal = parseFloat(this.dataset.cartTotal);

        if (cartTotal > 0 && cartTotal < maxThreshold) {
          let variantsList = product?.variants;

          if (protectionType === 'percentage') {
            const protectionPercentage =
              parseFloat(product?.variants[0]?.price) / 100;
            // remove first element that contains the percentage
            variantsList.shift();

            // Convert protection percentage to decimal
            const protectionPercentageDecimal = protectionPercentage / 100;

            // Calculate protection amount
            const protectionAmount = cartTotal * protectionPercentageDecimal;

            // Handle protection amount less than the minimum variant
            const minVariant = variantsList?.reduce((min, curr) => {
              return curr?.price / 100 < min?.price / 100 ? curr : min;
            });

            if (protectionAmount < minVariant?.price / 100) {
              return minVariant;
            }

            // Find the closest variant according to rounding type
            const closestVariant = variantsList.reduce((prev, curr) => {
              const currDiff = curr?.price / 100 - protectionAmount;
              const prevDiff = prev?.price / 100 - protectionAmount;

              if (roundingType === 'rounding-up') {
                return (currDiff >= 0 && currDiff < prevDiff) || prevDiff < 0
                  ? curr
                  : prev;
              } else if (roundingType === 'rounding-down') {
                return (currDiff <= 0 && currDiff > prevDiff) || prevDiff > 0
                  ? curr
                  : prev;
              } else {
                // Default to closest variant without rounding consideration
                return Math.abs(currDiff) < Math.abs(prevDiff) ? curr : prev;
              }
            });

            return closestVariant;
          } else {
            let applicableVariant = variantsList.find((variant, variantIndex) => {
              // expected formate is title: '10-20'
              const splitTitle = variant.title.split('-');

              const minVal = parseFloat(splitTitle[0]).toFixed(2);
              const maxVal = parseFloat(splitTitle[1]).toFixed(2);

              return parseFloat(cartTotal) >= minVal && parseFloat(cartTotal) <= maxVal;
            });

            // if the subtotal is greater then range of last variant then still return last variant.

            if (!applicableVariant) {
              const lastVariant = variantsList[variantsList.length - 1];
              const lastVariantMaxVal = parseFloat(lastVariant.title.split('-')[1]);

              if (parseFloat(cartTotal) > lastVariantMaxVal) {
                applicableVariant = lastVariant;
              } else {
                applicableVariant = null; // or undefined, based on your requirement
              }
            }

            return applicableVariant;
          }
        } else {
          return null;
        }
      }
    }
  );
}

if (!customElements.get('cart-donation')) {
  customElements.define(
    'cart-donation',
    class CartDonation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.inputs = this.querySelectorAll('input[readonly]');
        this.inputCustom = this.querySelector('input[type="number"]');
        this.addButton = this.querySelector('.cart-donation__add-button');

        this.inputs.forEach((input) => {
          input.addEventListener('click', () => {
            const qty = input.value.replace(/\D/g, '');
            this.addDonation(qty);
          });
        });
        
        this.addButton?.addEventListener('click', () => {
          const qty = this.inputCustom.value.replace(/\D/g, '');
          this.addDonation(qty);
        });

        this.inputCustom?.addEventListener('input', () => {
          this.addButton.parentElement.classList.toggle('hidden', this.inputCustom.value.length == 0);
        });
      }

      addDonation(qty = 1) {
        if (this.dataset.id === '') return;

        this.addButton.parentElement.classList.remove('hidden');
        this.addButton.disabled = true;
        this.addButton.textContent = 'Adding...';

        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-items');

        const body = JSON.stringify({
          id: this.dataset.id,
          quantity: qty,
          sections: this.cart.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_add_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            console.log(response.description);
            return;
          }

          this.classList.add('donation-successed');
          
          setTimeout(() => {
            if (this.cart) {
              this.cart.renderContents(response);
            } else {
              window.location = window.routes.cart_url;
            }
          }, 1500);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.addButton.parentElement.classList.add('hidden');
          this.addButton.disabled = false;
          this.addButton.textContent = 'Add';
        })
      }
    }
  );
}

if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-items');

        this.input = this.querySelector('.cart-discount__input');
        this.submit = this.querySelector('.cart-discount__button');
        this.remove = this.querySelector('.cart-discount__remove');
        this.error = this.querySelector('.cart__discount-error');

        this.submit?.addEventListener('click', this.applyDiscount.bind(this));
        this.remove?.addEventListener('click', this.removeDiscount.bind(this));
      }

      async applyDiscount() {
        try {
          const discountCode = this.input.value.trim();

          if (discountCode == '') {
            this.error.textContent = 'Please enter a discount code';
            return;
          } else {
            this.error.textContent = '';
          }

          this.submit.disabled = true;
          this.submit.textContent = 'Applying...';

          // const cartResponse = await fetch(`${routes.cart_url}.js`);
          // const cartResponseData = await cartResponse.json();

          // const body = JSON.stringify({ 
          //   'discount_code': discountCode, 
          //   'cart': cartResponseData
          // });

          // const fetchConfig = {
          //   method: 'POST',
          //   headers: {
          //     'Content-Type': 'application/json',
          //     'api-key': '1632a984cc7217f4d613f7702de84fca',
          //   },
          //   body
          // };
      
          // let response = await fetch('/apps/pf', fetchConfig);
          // response = await response.json();

          // if (response.statusCode != 200) {
          //   this.error.textContent = response.body.message;
          //   return;
          // }

          let discountResponse = await fetch('/discount/' + encodeURIComponent(discountCode), {
            method: 'GET',
            credentials: 'same-origin'
          });

          if (!discountResponse.ok) {
            this.error.textContent = 'Invalid discount code or could not be applied';
            return;
          }

          const cartResponse = await fetch(`${routes.cart_update_url}.js?sections=${this.cart.getSectionsToRender().map((section) => section.id)}`);
          const cart = await cartResponse.json();

          let discountApplied = false;

          cart.items.forEach((item) => {
            item.line_level_discount_allocations.forEach((discount) => {
              if (discount.discount_application.title.toLowerCase() === discountCode.toLowerCase()) {
                discountApplied = true;
              }
            });
          });

          cart.cart_level_discount_applications.forEach((discount) => {
            if (discount.discount_application.title.toLowerCase() === discountCode.toLowerCase()) {
              discountApplied = true;
            }
          });

          if (!discountApplied) {
            this.error.textContent = 'Invalid discount code or could not be applied';
            return;
          }

          this.cart.renderContents(cart);
        } catch (error) {
          console.log('Error', error);
          this.error.textContent = 'There is something wrong please try again';
        } finally {
          this.submit.disabled = false;
          this.submit.textContent = 'Apply';
        }
      }

      async removeDiscount() {
        try {
          this.remove.disabled = true;
          this.remove.textContent = 'Removing...';

          const response = await fetch('/discount/clear', {
            method: 'GET',
            credentials: 'same-origin'
          });

          if (!response.ok) {
            this.error.textContent = 'Failed to remove discount';
            return;
          }

          const cartResponse = await fetch(`${routes.cart_update_url}.js?sections=${this.cart.getSectionsToRender().map((section) => section.id)}`);
          const cartResponseJson = await cartResponse.json();

          this.cart.renderContents(cartResponseJson);
        } catch (error) {
          this.error.textContent = 'Failed to remove discount';
          console.error('Error removing discount:', error);
        } finally {
          this.remove.disabled = false;
          this.remove.textContent = 'Remove';
        }
      }
    }
  );
}

if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {

      constructor() {
        super();
      }

      connectedCallback() {
        this.loadRecommendations();
      }

      loadRecommendations() {
        let cartRecommendationUrl = `${window.Shopify.routes.root}?section_id=cart-recommendations`;

        if (this.dataset.productId) {
          cartRecommendationUrl = `${this.dataset.url}&product_id=${this.dataset.productId}&section_id=cart-recommendations`;
        }

        fetch(cartRecommendationUrl)
          .then((response) => response.text())
          .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const recommendations = html.querySelector('.shopify-section');

            if (recommendations?.innerHTML.trim().length) {
              this.classList.remove('hidden');

              const sliderEl = this.querySelector('.cart-recommendations__slider');
              sliderEl.querySelector('.swiper-wrapper').innerHTML = recommendations.innerHTML;

              const slider = new Swiper(sliderEl, {
                slidesPerView: 'auto',
                loop: true,
                watchSlidesProgress: true,
                pagination: {
                  el: '.swiper-pagination',
                  type: 'bullets',
                  clickable: true,
                }
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.classList.add('hidden');
          });
      }
    }
  );
}